import { svgCircle, svgLine, svgPath, svgRect, svgRoot } from '@tiendanube/nube-sdk-ui';
/**
 * The icon catalogue, as component trees instead of SVG markup strings.
 *
 * `form-renderer/src/icons.ts` stored each icon as a raw `<svg>…</svg>` string and injected it with
 * `innerHTML`. Neither exists here: there is no DOM to parse markup into, so every path, circle and
 * line is a component the host renders.
 *
 * Geometry is copied verbatim from the originals (a 24×24 Feather-style set) so the icons stay
 * pixel-identical between the legacy and NubeSDK bundles for as long as both ship — visual drift
 * between the two would be exactly the inconsistency `MIGRATION.md` §12.0 set out to avoid.
 *
 * `stroke="currentColor"` is preserved: it is what lets an icon inherit the surrounding text colour
 * rather than needing the merchant's palette threaded in.
 */
/** Shared stroke geometry — every icon in the set uses the same weight and joins. */
const STROKE = {
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
};
/**
 * Root wrapper at the size the original markup declared (18px for buttons, 16px for fields).
 *
 * `display: block` is **not cosmetic** — it is the fix the legacy CSS spelled out as
 * `.cod-button .cod-icon svg { display: block; }`. An SVG defaults to `display: inline`, which
 * seats it on the text baseline and reserves descender space below it, so next to vertically
 * centred text it renders visibly low. Dropping this rule in the port reproduced the exact
 * misalignment on a live store.
 *
 * `flexShrink: 0` keeps the glyph square when the label is long enough to compete for width.
 */
const root = (size, children) => svgRoot({
    viewBox: '0 0 24 24',
    width: size,
    height: size,
    fill: 'none',
    style: { display: 'block', flexShrink: 0 },
    children,
});
const p = (d) => svgPath({ d, ...STROKE });
/**
 * Every icon, keyed by the enum values in `config-schema`.
 *
 * Built lazily (functions, not values) so a config that uses one icon does not construct the other
 * nine on every re-render — the builder is re-invoked on each state change.
 */
const ICONS = {
    // Button icons — 18px.
    bag: () => root(18, [
        p('M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z'),
        p('M3 6h18'),
        p('M16 10a4 4 0 0 1-8 0'),
    ]),
    cart: () => root(18, [
        svgCircle({ cx: 9, cy: 21, r: 1, ...STROKE }),
        svgCircle({ cx: 20, cy: 21, r: 1, ...STROKE }),
        p('M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6'),
    ]),
    // Field icons — 16px.
    user: () => root(16, [
        p('M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2'),
        svgCircle({ cx: 12, cy: 7, r: 4, ...STROKE }),
    ]),
    phone: () => root(16, [
        p('M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z'),
    ]),
    pin: () => root(16, [
        p('M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z'),
        svgCircle({ cx: 12, cy: 10, r: 3, ...STROKE }),
    ]),
    hash: () => root(16, [
        svgLine({ x1: 4, y1: 9, x2: 20, y2: 9, ...STROKE }),
        svgLine({ x1: 4, y1: 15, x2: 20, y2: 15, ...STROKE }),
        svgLine({ x1: 10, y1: 3, x2: 8, y2: 21, ...STROKE }),
        svgLine({ x1: 16, y1: 3, x2: 14, y2: 21, ...STROKE }),
    ]),
    at: () => root(16, [
        svgCircle({ cx: 12, cy: 12, r: 4, ...STROKE }),
        p('M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94'),
    ]),
    mail: () => root(16, [
        svgRect({ x: 2, y: 4, width: 20, height: 16, rx: 2, ...STROKE }),
        p('m22 7-10 5L2 7'),
    ]),
    note: () => root(16, [
        p('M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'),
        p('M14 2v6h6'),
        p('M9 13h6'),
        p('M9 17h4'),
    ]),
    // Header glyph — 22px.
    home: () => root(22, [
        p('M3 9.5 12 3l9 6.5'),
        p('M5 10v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V10'),
        p('M9 21v-6h6v6'),
    ]),
    // Date-picker glyphs — 16px, matching the field icons they sit beside. NOT reachable from the
    // `FieldIcon` enum, so a merchant can never pick them; they are the picker's own chrome. Kept in
    // this map anyway so the catalogue's invariants (24×24 root, `display: block`, `currentColor`)
    // cover them too — the misalignment those rules prevent is exactly what a glyph inside an input
    // would show.
    calendar: () => root(16, [
        svgRect({ x: 3, y: 4, width: 18, height: 18, rx: 2, ...STROKE }),
        p('M16 2v4'),
        p('M8 2v4'),
        p('M3 10h18'),
    ]),
    chevronLeft: () => root(16, [p('m15 18-6-6 6-6')]),
    chevronRight: () => root(16, [p('m9 18 6-6-6-6')]),
};
/**
 * Look up an icon, or `null` when it is `'none'` or unrecognised.
 *
 * Returning `null` rather than an empty component mirrors the original's empty-string contract: a
 * new enum value added to `config-schema` before its glyph exists here renders nothing instead of
 * throwing. Consumers must handle `null` — the tree simply omits the icon.
 */
export function iconTree(name) {
    if (name === 'none')
        return null;
    return ICONS[name]?.() ?? null;
}
/** The home/store glyph shown in the form header. */
export const homeIcon = () => iconTree('home');
export const buttonIcon = (icon) => iconTree(icon);
export const fieldIcon = (icon) => iconTree(icon);
/** The glyph pinned inside a `date` field's control, which is what marks it as a picker. */
export const calendarIcon = () => iconTree('calendar');
/** The picker's month arrows. */
export const chevronLeftIcon = () => iconTree('chevronLeft');
export const chevronRightIcon = () => iconTree('chevronRight');
/** Every icon name with a glyph. Exported so tests can assert full enum coverage. */
export const ICON_NAMES = Object.keys(ICONS);
//# sourceMappingURL=icons.js.map